import { z } from 'zod';

/**
 * Coordinates schema that handles both Firestore GeoPoint and regular coordinate formats
 * Uses z.preprocess to transform GeoPoint objects before validation
 */
export const CoordinatesSchema = z.preprocess(
  (data: unknown) => {
    // Handle Firestore GeoPoint format with latitude/longitude properties
    if (data && typeof data === 'object' && 'latitude' in data && 'longitude' in data) {
      const geoPoint = data as { latitude: number; longitude: number };
      return { lat: geoPoint.latitude, lng: geoPoint.longitude };
    }
    // Return data as-is for regular lat/lng format
    return data;
  },
  z.object({ lat: z.number(), lng: z.number() }),
);

export interface Coordinates {
  lat: number;
  lng: number;
}

/**
 * Type guard to check if a value is a valid coordinates object
 */
export function isCoordinates(value: unknown): value is Coordinates {
  return CoordinatesSchema.safeParse(value).success;
}

/**
 * Cost schema for currency and amount values
 * Provides consistent validation for monetary values across the application
 */
export const CostSchema = z.object({ amount: z.number(), currency: z.string().default('USD') });

export interface Cost extends z.infer<typeof CostSchema> {}

/**
 * One labeled tax or fee line item from a hotel rate (Sabre `TaxGroup` / `FeeGroup`).
 * `label` is the GDS description (e.g. "Resort fee", "City / Municipal Tax"). Captured
 * alongside the aggregate `totalTax` / `totalFees` so the all-in price can be disclosed
 * line-by-line, as mandatory-fee rules require (FTC 16 CFR 464 / CA SB478, ELARA-829).
 */
export const LabeledChargeSchema = z.object({
  code: z.number().optional(),
  label: z.string().optional(),
  amount: z.number(),
  currency: z.string().optional(),
});

export type LabeledCharge = z.infer<typeof LabeledChargeSchema>;

/**
 * Optional cost schema for cases where monetary values are optional
 */
export const OptionalCostSchema = CostSchema.optional();

/**
 * Person name schema for first and last names
 * Provides consistent validation for person names across the application
 */
export const PersonNameSchema = z.object({ firstName: z.string().optional(), lastName: z.string().optional() });

export interface PersonName extends z.infer<typeof PersonNameSchema> {}

/**
 * Basic person name schema for cases where names don't need minimum length validation
 */
export const BasicPersonNameSchema = z.object({ firstName: z.string(), lastName: z.string() });

export interface BasicPersonName extends z.infer<typeof BasicPersonNameSchema> {}

/**
 * Address schema for location information
 * Provides consistent validation for addresses across the application
 *
 * Every field is optional and no combination is required — partial addresses are normal:
 * onboarding stores a city alone, profile editors save whatever the user chose to share,
 * and hotel / place records take whatever the Places API returned.
 */
export const AddressSchema = z.object({
  city: z.string().optional(),
  cityCode: z.string().optional(),
  country: z.string().optional(),
  countryCode: z.string().optional(), // Length validation handled in refinement
  line1: z.string().optional(),
  line2: z.string().optional(),
  postalCode: z.string().optional(),
  /** State or region ("Texas", "England", "Bali"); Sabre imports and the Add Hotel page (from Google) write it. */
  state: z.string().optional(),
  /** Its short code where one exists ("TX", "NV"). */
  stateCode: z.string().optional(),
});

export interface Address extends z.infer<typeof AddressSchema> {}

/**
 * Contact information schema
 * Provides consistent validation for contact details across the application
 */
export const ContactSchema = z.object({
  phone: z.string().optional(),
  fax: z.string().optional(),
  email: z.string().email().optional(),
});

export interface Contact extends z.infer<typeof ContactSchema> {}

/**
 * Location schema for shortlist and itinerary items
 * Accepts either coordinates (lat/lng) or a text address
 */
export const ItemLocationSchema = z
  .object({ label: z.string().optional(), coordinates: CoordinatesSchema.optional(), address: z.string().optional() })
  .refine(
    (data) => {
      // Must have either coordinates or address
      return !!(data.coordinates || data.address);
    },
    { message: 'Location must have either coordinates or address' },
  )
  .nullish();

export type ItemLocation = z.infer<typeof ItemLocationSchema>;

/**
 * A preference item with source tracking.
 * Source indicates whether the preference was explicitly provided by the user
 * or learned/stored by the AI agent.
 */
export const PreferenceSourceSchema = z.enum(['user', 'elara']);
export type PreferenceSource = z.infer<typeof PreferenceSourceSchema>;

/** Groups a saved preference so the concierge prompt can list related facts together. */
export const PreferenceCategorySchema = z.enum(['hotel', 'airport', 'destination', 'dining', 'dietary', 'key_date', 'general']);
export type PreferenceCategory = z.infer<typeof PreferenceCategorySchema>;
/** Categories whose Elara-saved entries name one verified record or a generic rule; the rest stay text. */
export const TARGETED_PREFERENCE_CATEGORIES = ['hotel', 'destination', 'dining'] as const;
export type TargetedPreferenceCategory = (typeof TARGETED_PREFERENCE_CATEGORIES)[number];
/** Narrows a category to the ones whose Elara-saved entries need a link or a generic flag. */
export function isTargetedPreferenceCategory(category: PreferenceCategory): category is TargetedPreferenceCategory {
  return (TARGETED_PREFERENCE_CATEGORIES as readonly PreferenceCategory[]).includes(category);
}
/** A catalogue hotel/destination ID or a Google-backed place record ID. */
export const PreferenceEntityRefSchema = z.looseObject({
  type: z.enum(['hotel', 'place', 'destination']).describe('Catalogue hotel, Google-backed place or curated destination.'),
  id: z.string().min(1).describe('The entity’s internal record ID, not a provider resource name.'),
});

export const TaggedPreferenceSchema = z.looseObject({
  value: z.string(),
  source: PreferenceSourceSchema.optional(),
});
export type TaggedPreference = z.infer<typeof TaggedPreferenceSchema>;

export const CategorizedPreferenceSchema = TaggedPreferenceSchema.extend({
  id: z.string().min(1),
  category: PreferenceCategorySchema,
  avoid: z.literal(true).optional(),
  entityRef: PreferenceEntityRefSchema.optional(),
  generic: z.literal(true).optional(),
}).refine((entry) => !entry.avoid || Boolean(entry.entityRef), {
  message: 'An avoided entry requires an entity reference.',
  path: ['entityRef'],
}).refine((entry) => entry.source !== 'elara' || !isTargetedPreferenceCategory(entry.category)
  || Boolean(entry.entityRef) !== Boolean(entry.generic), {
  message: 'A remembered hotel, destination or dining entry requires exactly one entity reference or generic rule.',
  path: ['entityRef'],
});
export type CategorizedPreference = z.infer<typeof CategorizedPreferenceSchema>;

const LegacyPreferenceSchema = TaggedPreferenceSchema.refine(
  (entry) => !('id' in entry || 'category' in entry || 'avoid' in entry || 'entityRef' in entry || 'generic' in entry),
);
/** Old `{ value, source }` entries still parse; an entry with any new key must have the full new shape. */
export const ProfilePreferencesSchema = z.array(z.union([CategorizedPreferenceSchema, LegacyPreferenceSchema])).optional();

/** Helper: a Zod schema for an array of tagged preferences */
export const TaggedPreferencesSchema = z.array(TaggedPreferenceSchema).optional();

/**
 * Who last touched a trip item: the traveler in the app, the AI concierge on the traveler's behalf
 * (a tool call inside a chat turn), or Elara staff through the internal MCP. Stored on itinerary and
 * shortlist items so the app can show whose addition or edit it was.
 */
export const ItemActorSchema = z.enum(['traveler', 'elara', 'staff']);

export type ItemActor = z.infer<typeof ItemActorSchema>;
